import React, { useEffect, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { DesktopKpiStrip } from "../shared/DesktopKpiStrip";
import { DeskEmpty, DeskPanel, DeskTable } from "../desktop/DeskTable";
import { CPDropdown } from "../CPDropdown";
import { CPInput } from "../CPInput";
import { formatHoursMinutes } from "../../helpers/timeHelpers";
import { formatSessionShiftLabel } from "../../helpers/dateTimeHelpers";
import { formatPeriodLabel } from "../../helpers/payrollReport";
import { TIME_LOG_DATE_RANGE_OPTIONS } from "../../helpers/timeLogVisibility";
import "./payrollDesktop.css";

function formatDay(iso) {
  if (!iso) return "—";
  const date = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatBreaks(seconds) {
  const s = seconds || 0;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

function useWideViewport() {
  const read = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(min-width: 1280px)").matches
      : true;
  const [wide, setWide] = useState(read);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia("(min-width: 1280px)");
    const onChange = () => setWide(media.matches);
    media.addEventListener?.("change", onChange);
    return () => media.removeEventListener?.("change", onChange);
  }, []);
  return wide;
}

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

/** Payroll landing (desktop): one real destination, plus the viewer's access. */
export function PayrollHomeDesktop({ onBack, scopeLabel, isSelfScope, canMarkPaid, onOpenQueue }) {
  return (
    <CrewPilotModuleShell
      title="Payroll"
      subtitle={scopeLabel}
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
      primary
      compact
      headerActions={
        <div className="cdh-actions">
          <button type="button" className="dt-btn dt-btn--primary" onClick={onOpenQueue}>
            Open Payroll Queue
          </button>
        </div>
      }
    >
      <div className="dp-grid dp-grid--main-side">
        <DeskPanel>
          <div className="pay-feature">
            <div>
              <h3>Payroll Queue</h3>
              <p>
                {isSelfScope
                  ? "Review your payroll entries and pay period status."
                  : "Process payroll entries, mark paid/unpaid, and manage payroll status."}
              </p>
            </div>
            <button type="button" className="dt-btn" onClick={onOpenQueue}>
              Open Queue →
            </button>
          </div>
        </DeskPanel>
        <DeskPanel title="Your access">
          <dl className="dp-kv dp-kv--tight">
            <dt>Scope</dt>
            <dd>{scopeLabel}</dd>
            <dt>Mark paid</dt>
            <dd>{canMarkPaid ? "Allowed" : "View only"}</dd>
          </dl>
        </DeskPanel>
      </div>
    </CrewPilotModuleShell>
  );
}

/**
 * Payroll queue (desktop): filter bar, KPI strip, bulk-action toolbar and an
 * employee-grouped entries table. All state and handlers live in the screen.
 */
export function PayrollQueueDesktop({
  onBack,
  dateRangeFilter,
  onDateRange,
  startDate,
  endDate,
  onStartDate,
  onEndDate,
  statusFilter,
  onStatus,
  showScopeSelector,
  selectedEmployee,
  onEmployee,
  payrollScope,
  scopeValues,
  employees,
  isLoading,
  onLoad,
  rows,
  groupedRows,
  employeePayrollTotals,
  selectedRows,
  setSelectedRows,
  canMarkPaid,
  onToggleStatus,
  onMarkSelected,
  onExport,
  onSave,
  isSaving,
  hasLoaded,
}) {
  const [collapsed, setCollapsed] = useState({});
  const wide = useWideViewport();
  const rangeInvalid = Boolean(startDate && endDate && startDate > endDate);
  const unsaved = rows.filter((row) => row.payroll_status !== row.original_payroll_status).length;
  const groups = Object.entries(groupedRows);
  const allSelected = rows.length > 0 && selectedRows.length === rows.length;

  const toggleOne = (id) =>
    setSelectedRows((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleGroup = (ids) =>
    setSelectedRows((prev) =>
      ids.every((id) => prev.includes(id))
        ? prev.filter((id) => !ids.includes(id))
        : [...new Set([...prev, ...ids])],
    );

  const columns = [
    {
      key: "sel",
      label: "",
      width: "44px",
      align: "center",
      render: (row) => (
        <input
          type="checkbox"
          className="pay-check"
          checked={selectedRows.includes(row.id)}
          onChange={() => toggleOne(row.id)}
          aria-label={`Select ${formatDay(row.date_iso)} ${row.project_name || "entry"}`}
        />
      ),
    },
    { key: "date", label: "Date", width: "118px", render: (row) => <span className="dt-nowrap">{formatDay(row.date_iso)}</span> },
    {
      key: "project",
      label: "Project",
      render: (row) => (
        <>
          <span className="dt-strong">{row.project_name || "No Project"}</span>
          <span className="dt-sub">
            {row.trade_name ? `${row.trade_name} • ` : ""}
            {row.classification_name || "No Classification"} • {row.payroll_type || "No Payroll Type"}
          </span>
        </>
      ),
    },
    {
      key: "shift",
      label: "Shift",
      width: "170px",
      render: (row) => (
        <span className="dt-muted dt-nowrap">
          {row.start_time && row.end_time ? formatSessionShiftLabel(row.start_time, row.end_time) : "N/A"}
        </span>
      ),
    },
    { key: "hrs", label: "Paid", align: "right", width: "84px", render: (row) => <span className="dt-num">{formatHoursMinutes(row.total_hours)}</span> },
    { key: "brk", label: "Breaks", align: "right", width: "78px", render: (row) => <span className="dt-muted dt-nowrap">{formatBreaks(row.break_seconds)}</span> },
    { key: "rate", label: "Rate", align: "right", width: "104px", render: (row) => <span className="dt-muted dt-nowrap">{money(row.hourly_rate)}/hr</span> },
    { key: "gross", label: "Gross", align: "right", width: "104px", render: (row) => <span className="dt-num">{money(row.gross_pay)}</span> },
    {
      key: "status",
      label: "Status",
      width: "104px",
      align: "center",
      render: (row) => (
        <button
          type="button"
          className={`pay-status pay-status--${row.payroll_status === "paid" ? "paid" : "unpaid"}`}
          onClick={() => onToggleStatus(row.id)}
          aria-label={`Status ${row.payroll_status === "paid" ? "paid" : "unpaid"}, click to toggle`}
        >
          {row.payroll_status === "paid" ? "PAID" : "UNPAID"}
        </button>
      ),
    },
  ];

  const NARROW_HIDDEN = ["shift", "brk", "rate"];
  const visibleColumns = wide ? columns : columns.filter((c) => !NARROW_HIDDEN.includes(c.key));

  const tableGroups = groups.map(([key, data]) => {
    const totals = employeePayrollTotals[key] || { totalHours: 0, regularHours: 0, overtimeHours: 0, grossPay: 0 };
    const ids = data.rows.map((r) => r.id);
    const open = !collapsed[key];
    return {
      key,
      rows: open ? data.rows : [],
      header: (
        <div className="pay-group">
          <input
            type="checkbox"
            className="pay-check"
            checked={ids.every((id) => selectedRows.includes(id))}
            onChange={() => toggleGroup(ids)}
            aria-label={`Select all entries for ${data.employeeName}`}
          />
          <button
            type="button"
            className="pay-group__toggle"
            aria-expanded={open}
            onClick={() => setCollapsed((prev) => ({ ...prev, [key]: open }))}
          >
            <span className={`pay-group__chev${open ? " is-open" : ""}`} aria-hidden="true">▶</span>
            {data.employeeName}
          </button>
          <div className="pay-group__meta">
            <span>Entries <b>{data.rows.length}</b></span>
            <span>Hours <b>{formatHoursMinutes(totals.totalHours)}</b></span>
            {totals.overtimeHours > 0 ? (
              <span>Regular <b>{totals.regularHours.toFixed(1)}</b> · Overtime <b>{totals.overtimeHours.toFixed(1)}</b></span>
            ) : null}
            <span className="pay-group__gross">Gross Payroll {money(totals.grossPay)}</span>
          </div>
        </div>
      ),
    };
  });

  return (
    <CrewPilotModuleShell
      title="Payroll Queue"
      subtitle="Payroll Processing"
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
      compact
    >
      <DeskPanel title="Payroll period">
        <div className="pay-filters">
          <div className="pay-filters__field">
            <CPDropdown
              label="Date Range"
              value={dateRangeFilter}
              onChange={onDateRange}
              includePlaceholder={false}
              options={TIME_LOG_DATE_RANGE_OPTIONS}
            />
          </div>
          {dateRangeFilter === "custom_range" ? (
            <>
              <div className="pay-filters__field">
                <CPInput label="Start Date" type="date" value={startDate} onChange={onStartDate} />
              </div>
              <div className="pay-filters__field">
                <CPInput label="End Date" type="date" value={endDate} onChange={onEndDate} />
              </div>
            </>
          ) : null}
          <div className="pay-filters__field">
            <CPDropdown
              label="Status"
              value={statusFilter}
              onChange={onStatus}
              includePlaceholder={false}
              options={[
                { value: "unpaid", label: "Unpaid" },
                { value: "paid", label: "Paid" },
                { value: "all", label: "All" },
              ]}
            />
          </div>
          {showScopeSelector ? (
            <div className="pay-filters__field">
              <CPDropdown
                label="Scope"
                value={selectedEmployee}
                onChange={onEmployee}
                includePlaceholder={false}
                options={[
                  ...(payrollScope === scopeValues.team
                    ? [
                        { value: "MY_CREW", label: "My Crew" },
                        { value: "", label: "My Time" },
                      ]
                    : []),
                  ...(payrollScope === scopeValues.organization
                    ? [
                        { value: "ALL_EMPLOYEES", label: "All Employees" },
                        { value: "", label: "My Time" },
                      ]
                    : []),
                  ...employees.map((employee) => ({
                    value: employee.supabase_user_id,
                    label: employee.employee_name,
                  })),
                ]}
              />
            </div>
          ) : null}
          <button
            type="button"
            className="dt-btn dt-btn--primary pay-filters__go"
            onClick={onLoad}
            disabled={!startDate || !endDate || rangeInvalid || isLoading}
          >
            {isLoading ? "Loading..." : "Load Payroll Queue"}
          </button>
        </div>
        {dateRangeFilter === "custom_range" ? (
          <p className="dp-hint" style={{ margin: "12px 0 0" }}>
            Custom Range uses the Start and End dates as the loaded payroll period.
          </p>
        ) : null}
        {rangeInvalid ? (
          <p role="alert" className="pay-alert">
            Start must be on or before End.
          </p>
        ) : null}
        {startDate || endDate ? (
          <div className="pay-period" aria-label="Payroll period">
            Period: <strong>{formatPeriodLabel(startDate, endDate)}</strong>
          </div>
        ) : null}
      </DeskPanel>

      <div style={{ height: 18 }} />

      {rows.length > 0 ? (
        <>
          <DesktopKpiStrip
            ariaLabel="Queue summary"
            tiles={[
              { key: "entries", value: String(rows.length), label: "Entries", tone: "gold" },
              { key: "emp", value: String(groups.length), label: groups.length === 1 ? "Employee" : "Employees", tone: "blue" },
              { key: "sel", value: String(selectedRows.length), label: "Selected", tone: "green" },
              { key: "uns", value: String(unsaved), label: "Unsaved Changes", tone: unsaved ? "red" : "muted" },
            ]}
          />
          <div className="pay-bar" role="toolbar" aria-label="Payroll queue actions">
            <button type="button" className="dt-btn" onClick={() => setSelectedRows(allSelected ? [] : rows.map((r) => r.id))}>
              {allSelected ? "Clear All" : "Select All"}
            </button>
            <span className="dt-summary" aria-live="polite">{selectedRows.length} selected</span>
            {selectedRows.length > 0 && canMarkPaid ? (
              <>
                <span className="pay-bar__sep" aria-hidden="true" />
                <button type="button" className="dt-btn" onClick={() => onMarkSelected("paid")}>Mark Paid</button>
                <button type="button" className="dt-btn" onClick={() => onMarkSelected("unpaid")}>Mark Unpaid</button>
              </>
            ) : null}
            <span className="pay-bar__grow" />
            <button type="button" className="dt-btn" onClick={onExport}>Export Report</button>
            <button type="button" className="dt-btn dt-btn--primary" onClick={onSave} disabled={isSaving}>
              {isSaving ? "Saving..." : unsaved ? `Save Changes (${unsaved})` : "Save Changes"}
            </button>
          </div>
          <DeskTable
            className="pay-table"
            columns={visibleColumns}
            groups={tableGroups}
            rowKey={(row) => row.id}
            caption="Payroll queue entries"
          />
        </>
      ) : hasLoaded && !isLoading ? (
        <div className="dt-card">
          <DeskEmpty title="No payroll entries">No entries match this period, status and scope.</DeskEmpty>
        </div>
      ) : (
        <div className="dt-card">
          <DeskEmpty title="Choose a period">Select a date range and load the queue to review entries.</DeskEmpty>
        </div>
      )}
    </CrewPilotModuleShell>
  );
}

/** Payroll report setup (desktop): readable form with side-by-side fields. */
export function PayrollReportFormDesktop({
  onBack,
  employeeName,
  startDate,
  endDate,
  onStartDate,
  onEndDate,
  reportScope,
  onScope,
  scopeOptions,
  showEmployee,
  selectedEmployee,
  onEmployee,
  employees,
  onGenerate,
  isGenerating,
}) {
  return (
    <CrewPilotModuleShell
      title="Payroll Reports"
      subtitle={employeeName}
      onBack={onBack}
      layout={PAGE_LAYOUT.FORM}
      compact
    >
      <DeskPanel title="Report setup">
        <div className="pay-form">
          <div className="pay-form__grid">
            <div>
              <CPInput label="Start Date" type="date" value={startDate} onChange={onStartDate} />
              {startDate ? <p className="pay-form__hint">{formatDay(startDate)}</p> : null}
            </div>
            <div>
              <CPInput label="End Date" type="date" value={endDate} onChange={onEndDate} />
              {endDate ? <p className="pay-form__hint">{formatDay(endDate)}</p> : null}
            </div>
            <CPDropdown label="Scope" value={reportScope} onChange={onScope} includePlaceholder={false} options={scopeOptions} />
            {showEmployee ? (
              <CPDropdown
                label="Employee"
                value={selectedEmployee}
                onChange={onEmployee}
                options={[
                  { value: "", label: "Select Employee" },
                  ...employees.map((employee) => ({ value: employee.supabase_user_id, label: employee.employee_name })),
                ]}
              />
            ) : null}
          </div>
          <div>
            <button
              type="button"
              className="dt-btn dt-btn--primary"
              onClick={onGenerate}
              disabled={!startDate || !endDate || isGenerating}
            >
              {isGenerating ? "Generating..." : "Generate Payroll Report"}
            </button>
          </div>
        </div>
      </DeskPanel>
    </CrewPilotModuleShell>
  );
}
